import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { ChevronRight, HelpCircle } from '../../../icons';
// 범위 칩(2026-10-17 · web 레인 W6) — «관리자 화면» · «내 것»
import { Shield, User } from '../../../icons';
// 경로 링크(2026-10-17 · web 레인 W7b) — breadcrumbLinks 를 줄 때만 그린다
import { Link } from 'react-router-dom';
import './PageHeader.css';

/**
 * PageHeader — 화면의 제목이 서는 자리 (디자인 정본 A3).
 *
 * ★★★ **왜 지금 필요한가 — 상단바가 렌더되지 않는다.**
 *    `AppLayout.tsx` 가 `SHELL_STATUSBAR_ENABLED` 로 `TopBar` 를 끄고 있고 그 플래그가 기본 켬이다.
 *    **제목·설명을 갖고 있던 자리가 사라졌으니** 제목은 본문 안으로 들어와야 하고, 도움말도 거기 붙는다.
 *
 * ★★ **메뉴 이름 = 화면 제목.** 레일에서 「내 근태」를 눌렀는데 화면이 「근태 현황」이면
 *    같은 곳인지 알 수 없다. (어긋난 11곳이 있었고 붙잡는 검사가 0이었다.)
 *
 * ★ 액션은 **최대 3개**. 그 이상은 화면이 무엇을 하는 곳인지 흐려진다.
 */
export interface PageHeaderProps {
  /** 지금 화면 제목. 레일 메뉴 이름과 같아야 한다. */
  title: ReactNode;
  /** 위쪽 경로. `['근태', '내 근태']` 처럼 마지막이 지금 화면이다. */
  breadcrumb?: ReadonlyArray<string>;
  /** 한 줄 설명. 「원래 그런 규칙」은 여기가 아니라 도움말로. */
  description?: ReactNode;
  /** 오른쪽 단추들 — 최대 3개. */
  actions?: ReactNode;
  /**
   * 이 화면의 도움말. **없으면 아이콘을 안 그린다** —
   * 눌렀는데 「도움말이 없습니다」가 뜨면 안 된다(정본 A3).
   */
  help?: { title?: ReactNode; body: ReactNode; actions?: ReactNode };
  className?: string;
  /**
   * 범위 칩 — 부제 앞에 «관리자 화면»(회사 전체를 바꾸는 화면) · «내 것»(본인 화면)을 단다.
   * 담당자 화면과 본인 화면이 같은 모양 · 비슷한 이름일 때 «지금 누구 것을 바꾸나» 를 제목 한 줄로만
   * 가르던 것을 막는다(평 2차 «3. 권한» · 시안 shellC head scope). 안 주면 지금과 같다.
   */
  scope?: 'admin' | 'me';
  /**
   * 경로 칸마다 갈 주소(같은 차례) — 준 칸만 링크가 된다. 마지막 칸(지금 화면)은 주소가 있어도 링크가 아니다.
   * ★ 안 주면 지금과 «똑같은» 마크업이다(PageHeader.links.test 가 못 박는다). 링크는 라우터 안에서만 그린다.
   * (2026-10-17 · web 레인 W7b — 입사 처리의 «직원 관리» 처럼 옛 손짠 경로가 갖던 링크를 지키려는 것)
   */
  breadcrumbLinks?: ReadonlyArray<string | undefined>;
}

export function PageHeader({ title, breadcrumb, description, actions, help, className, scope, breadcrumbLinks }: PageHeaderProps) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement>(null);
  const helpBtnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => { if (!wrapRef.current?.contains(e.target as Node)) setOpen(false); };
    /* ★ Esc 는 닫기만 하는 것이 아니라 **트리거로 돌아온다**(정본 A3).
          안 돌려보내면 포커스가 사라진 자리에 남아 다음 Tab 이 화면 맨 앞으로 튄다. */
    const onEsc = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      setOpen(false);
      helpBtnRef.current?.focus();
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onEsc);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onEsc);
    };
  }, [open]);

  return (
    <header className={['wc-page-head', className ?? ''].filter(Boolean).join(' ')}>
      {breadcrumb && breadcrumb.length > 0 && (
        <nav className="wc-page-head__crumb" aria-label="현재 위치">
          {breadcrumb.map((c, i) => (
            <span key={`${c}-${i}`} className="wc-page-head__crumb-item">
              {i > 0 && <ChevronRight className="wc-page-head__crumb-sep" aria-hidden />}
              {/* 마지막은 지금 화면이라 링크가 아니다 — 누르면 같은 자리로 가는 링크는 거짓말이다. */}
              {breadcrumbLinks?.[i] && i !== breadcrumb.length - 1
                ? <Link className="wc-page-head__crumb-link" to={breadcrumbLinks[i]!}>{c}</Link>
                : <span aria-current={i === breadcrumb.length - 1 ? 'page' : undefined}>{c}</span>}
            </span>
          ))}
        </nav>
      )}

      <div className="wc-page-head__row">
        <div className="wc-page-head__titles" ref={wrapRef}>
          <h1 className="wc-page-head__title">
            {title}
            {help && (
              /* 클릭으로 연다 — 호버가 아니다. 읽을 시간이 필요한 글이라 손을 떼면 사라지면 안 된다. */
              <button
                ref={helpBtnRef}
                type="button"
                className="wc-page-head__help-btn"
                aria-label="이 화면 도움말"
                aria-expanded={open}
                aria-controls={open ? `${id}-help` : undefined}
                onClick={() => setOpen((v) => !v)}
              >
                <HelpCircle aria-hidden />
              </button>
            )}
          </h1>
          {(description != null || scope) && (
            <p className="wc-page-head__desc">
              {scope && (
                <span className={`wc-page-head__scope wc-page-head__scope--${scope}`}>
                  {scope === 'admin' ? <Shield aria-hidden /> : <User aria-hidden />}
                  {scope === 'admin' ? '관리자 화면' : '내 것'}
                </span>
              )}
              {description}
            </p>
          )}

          {open && help && (
            /* ★★ **도움말은 모달이 아니다.** `role="dialog"` 는 「초점을 가두는 창」이라고
                   말하는 것인데 여기는 트랩이 없다 — 읽어 주는 쪽이 갇힌 줄 알고 빠져나오지
                   못한다(정본 P5: 팝오버는 트랩 없음 · Esc + 트리거 복귀). 읽는 자리이므로 `note`. */
            <div className="wc-page-head__help" id={`${id}-help`} role="note" aria-label="도움말">
              {help.title != null && <p className="wc-page-head__help-title">{help.title}</p>}
              <div className="wc-page-head__help-body">{help.body}</div>
              {help.actions && <div className="wc-page-head__help-acts">{help.actions}</div>}
            </div>
          )}
        </div>

        {actions && <div className="wc-page-head__actions">{actions}</div>}
      </div>
    </header>
  );
}
